import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

interface SignUpState {
  step: 'email' | 'mobile' | 'verification' | 'username';
  email: string;
  mobile: string;
  username: string;
  verificationCode: string;
}

export const SignUp: React.FC = () => {
  const history = useHistory();
  const [state, setState] = useState<SignUpState>({
    step: 'email',
    email: '',
    mobile: '',
    username: '',
    verificationCode: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const extractUsernameFromEmail = (email: string): string => {
    const prefix = email.split('@')[0];
    return prefix.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  };

  // Ultra-defensive input handlers with useCallback
  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e || !e.target || typeof e.target.value !== 'string') {
      console.error('Invalid email event or target');
      return;
    }
    
    const value = e.target.value;
    setState(prevState => ({ ...prevState, email: value }));
  }, []);

  const handleMobileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e || !e.target || typeof e.target.value !== 'string') {
      console.error('Invalid mobile event or target');
      return;
    }
    
    const value = e.target.value;
    setState(prevState => ({ ...prevState, mobile: value }));
  }, []);

  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e || !e.target || typeof e.target.value !== 'string') {
      console.error('Invalid verification code event');
      return;
    }
    
    const value = e.target.value;
    setState(prevState => ({ ...prevState, verificationCode: value }));
  }, []);

  const handleUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e || !e.target || typeof e.target.value !== 'string') {
      console.error('Invalid username event or target');
      return;
    }
    
    const value = e.target.value;
    setState(prevState => ({ ...prevState, username: value }));
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.email) return;
    
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
    
    setState(prev => ({ ...prev, step: 'mobile' }));
  };

  const handleMobileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.mobile) return;
    
    setIsLoading(true);
    // Simulate API call to send verification code
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
    
    setState(prev => ({ ...prev, step: 'verification' }));
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.verificationCode) return;
    
    setIsLoading(true);
    // Simulate verification
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
    
    const suggestedUsername = extractUsernameFromEmail(state.email);
    setState(prev => ({ 
      ...prev, 
      step: 'username',
      username: suggestedUsername 
    }));
  };

  const handleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.username) return;
    
    setIsLoading(true);
    // Simulate account creation
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
    
    // Redirect to dashboard
    history.push('/dashboard');
  };

  const renderEmailStep = () => (
    <div className="auth-step">
      <div className="step-header">
        <h2>Create Your Account</h2>
        <p>Let's start with your email address</p>
      </div>
      <form onSubmit={handleEmailSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            id="email"
            type="email"
            value={state.email}
            onChange={handleEmailChange}
            placeholder="Enter your email"
            required
            autoFocus
          />
        </div>
        <button type="submit" disabled={isLoading} className="auth-button">
          {isLoading ? 'Processing...' : 'Continue'}
        </button>
      </form>
    </div>
  );

  const renderMobileStep = () => (
    <div className="auth-step">
      <div className="step-header">
        <h2>Add Your Phone Number</h2>
        <p>We'll send you a verification code</p>
      </div>
      <form onSubmit={handleMobileSubmit}>
        <div className="form-group">
          <label htmlFor="mobile">Mobile Number</label>
          <input
            id="mobile"
            type="tel"
            value={state.mobile}
            onChange={handleMobileChange}
            placeholder="Enter your mobile number"
            required
            autoFocus
          />
        </div>
        <button type="submit" disabled={isLoading} className="auth-button">
          {isLoading ? 'Sending Code...' : 'Send Verification Code'}
        </button>
        <button 
          type="button" 
          onClick={() => setState(prev => ({ ...prev, step: 'email' }))}
          className="auth-button-secondary"
        >
          Back
        </button>
      </form>
    </div>
  );

  const renderVerificationStep = () => (
    <div className="auth-step">
      <div className="step-header">
        <h2>Verify Your Phone</h2>
        <p>Enter the verification code sent to {state.mobile}</p>
      </div>
      <form onSubmit={handleVerificationSubmit}>
        <div className="form-group">
          <label htmlFor="code">Verification Code</label>
          <input
            id="code"
            type="text"
            value={state.verificationCode}
            onChange={handleCodeChange}
            placeholder="Enter 6-digit code"
            maxLength={6}
            required
            autoFocus
          />
        </div>
        <button type="submit" disabled={isLoading} className="auth-button">
          {isLoading ? 'Verifying...' : 'Verify Code'}
        </button>
        <button 
          type="button" 
          onClick={() => setState(prev => ({ ...prev, step: 'mobile' }))}
          className="auth-button-secondary"
        >
          Back
        </button>
      </form>
    </div>
  );

  const renderUsernameStep = () => (
    <div className="auth-step">
      <div className="step-header">
        <h2>Choose Your Username</h2>
        <p>This is how others will find you</p>
      </div>
      <form onSubmit={handleUsernameSubmit}>
        <div className="form-group">
          <label htmlFor="username">Username</label>
          <input
            id="username"
            type="text"
            value={state.username}
            onChange={handleUsernameChange}
            placeholder="Enter username"
            required
            autoFocus
          />
        </div>
        <button type="submit" disabled={isLoading} className="auth-button">
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </button>
        <button 
          type="button" 
          onClick={() => setState(prev => ({ ...prev, step: 'verification' }))}
          className="auth-button-secondary"
        >
          Back
        </button>
      </form>
    </div>
  );

  const getStepContent = () => {
    switch (state.step) {
      case 'email': return renderEmailStep();
      case 'mobile': return renderMobileStep();
      case 'verification': return renderVerificationStep();
      case 'username': return renderUsernameStep();
      default: return renderEmailStep();
    }
  };

  const getStepNumber = () => {
    switch (state.step) {
      case 'email': return 1;
      case 'mobile': return 2;
      case 'verification': return 3;
      case 'username': return 4;
      default: return 1;
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="progress-bar">
          <div className="progress-indicator">
            Step {getStepNumber()} of 4
          </div>
          <div className="progress-line">
            <div 
              className="progress-fill" 
              style={{ width: `${(getStepNumber() / 4) * 100}%` }}
            />
          </div>
        </div>
        
        {getStepContent()}
        
        <div className="auth-footer">
          <p>Already have an account? <a href="/login">Sign in</a></p>
          <p><a href="/">← Back to Home</a></p>
        </div>
      </div>
    </div>
  );
};